import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, Plus, Trash2, RefreshCw, Wand2 } from 'lucide-react';

const DATA_TYPES = ['string', 'number', 'currency', 'date', 'boolean'];
const CATEGORIES = ['general', 'financial', 'location', 'time', 'custom'];

function guessDataType(fieldName) {
  const f = fieldName.toLowerCase();
  if (/amount|price|cost|fee|total|revenue|sales|value|salary|ctc|budget/.test(f)) return 'currency';
  if (/date|_at$|_on$|time|day|month|year/.test(f)) return 'date';
  if (/count|qty|quantity|num_|number|age|score|rank/.test(f)) return 'number';
  if (/^is_|^has_|active|enabled|flag|bool/.test(f)) return 'boolean';
  return 'string';
}

function guessCategory(fieldName) {
  const f = fieldName.toLowerCase();
  if (/amount|price|cost|fee|total|revenue|sales|value|salary|ctc|budget/.test(f)) return 'financial';
  if (/region|city|state|country|location|area|zone|district/.test(f)) return 'location';
  if (/date|time|day|month|year|period/.test(f)) return 'time';
  return 'general';
}

function makeRow(fieldName) {
  return {
    source_field: fieldName,
    source_label: fieldName,
    target_field: fieldName,
    target_label: fieldName,
    data_type: guessDataType(fieldName),
    is_required: false,
    default_value: null,
    category: guessCategory(fieldName)
  };
}

export default function FieldMappingPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [unmappedAction, setUnmappedAction] = useState('store');
  const [sourceFields, setSourceFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [formRes, mappingRes] = await Promise.all([
          api.get(`/forms/${formId}`),
          api.get(`/forms/${formId}/mappings`)
        ]);
        setForm(formRes.data);
        setSourceFields(formRes.data.sample_fields || []);
        if (mappingRes.data.mappings?.length > 0) {
          setMappings(mappingRes.data.mappings);
        } else if (formRes.data.sample_fields?.length > 0) {
          setMappings(formRes.data.sample_fields.map(f => makeRow(f)));
        }
        setUnmappedAction(mappingRes.data.unmapped_fields_action || 'store');
      } catch {
        toast.error('Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [formId]);

  const updateMapping = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  const addMapping = () => {
    setMappings([...mappings, {
      source_field: '',
      source_label: '',
      target_field: '',
      target_label: '',
      data_type: 'string',
      is_required: false,
      default_value: null,
      category: 'general'
    }]);
  };

  const removeMapping = (index) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validMappings = mappings.filter(m => m.source_field && m.target_field);
      await api.put(`/forms/${formId}/mappings`, {
        mappings: validMappings,
        unmapped_fields_action: unmappedAction
      });
      toast.success('Field mappings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDetectFields = async () => {
    setDetecting(true);
    try {
      const res = await api.get(`/forms/${formId}/detected-fields`);
      const detected = res.data.detected_fields || [];
      if (detected.length === 0) {
        toast.warning('No fields detected. Send a test webhook submission first.');
        return;
      }
      // Merge with existing sourceFields (avoid duplicates)
      const merged = Array.from(new Set([...sourceFields, ...detected]));
      setSourceFields(merged);
      // Pre-populate empty mappings for any new fields not yet mapped
      const alreadyMappedSources = new Set(mappings.map(m => m.source_field));
      const newFields = detected.filter(f => !alreadyMappedSources.has(f));
      if (newFields.length > 0) {
        setMappings(prev => [...prev, ...newFields.map(f => makeRow(f))]);
      }
      toast.success(`Detected ${detected.length} field(s) from ${res.data.submission_count} submission(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to detect fields');
    } finally {
      setDetecting(false);
    }
  };

  const handleSaveAndReprocess = async () => {
    setReprocessing(true);
    try {
      const validMappings = mappings.filter(m => m.source_field && m.target_field);
      if (validMappings.length === 0) {
        toast.error('Add at least one valid mapping before re-processing');
        return;
      }
      await api.put(`/forms/${formId}/mappings`, {
        mappings: validMappings,
        unmapped_fields_action: unmappedAction
      });
      const res = await api.post(`/forms/${formId}/reprocess`);
      toast.success(`Mappings saved & ${res.data.processed} submission(s) re-processed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save or re-process');
    } finally {
      setReprocessing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="font-data text-sm text-muted-foreground">Loading...</p></div>;
  if (!form) return <div className="text-center py-20 text-muted-foreground">Form not found</div>;

  return (
    <div data-testid="field-mapping-page">
      <Button variant="ghost" className="mb-4 -ml-2 text-muted-foreground rounded-sm" onClick={() => navigate('/webhooks')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Webhooks
      </Button>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Field Mapping: {form.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Map incoming Zoho fields to internal fields &middot; {sourceFields.length > 0 ? `${sourceFields.length} source fields detected` : 'No source fields detected yet - send a test webhook first'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="detect-fields-btn"
            variant="outline"
            className="rounded-sm text-xs"
            onClick={handleDetectFields}
            disabled={detecting}
          >
            <Wand2 className="w-3 h-3 mr-1" /> {detecting ? 'Detecting...' : 'Detect Fields'}
          </Button>
          <Button
            data-testid="save-reprocess-btn"
            variant="outline"
            className="rounded-sm text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={handleSaveAndReprocess}
            disabled={reprocessing || saving}
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${reprocessing ? 'animate-spin' : ''}`} />
            {reprocessing ? 'Re-processing...' : 'Save & Re-process'}
          </Button>
          <Button data-testid="save-mappings-btn" className="rounded-sm" onClick={handleSave} disabled={saving || reprocessing}>
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Mappings'}
          </Button>
        </div>
      </div>

      <Card className="border border-border rounded-sm shadow-sm mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base font-semibold">Mapping Configuration</CardTitle>
            <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={addMapping} data-testid="add-mapping-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Field
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Header */}
          <div className="grid grid-cols-12 gap-3 mb-3 px-1">
            <div className="col-span-3"><span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Source (Zoho)</span></div>
            <div className="col-span-1 flex items-center justify-center"><ArrowRight className="w-3 h-3 text-muted-foreground" /></div>
            <div className="col-span-3"><span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Target (Internal)</span></div>
            <div className="col-span-2"><span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Data Type</span></div>
            <div className="col-span-2"><span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Category</span></div>
            <div className="col-span-1" />
          </div>

          {/* Mapping Rows */}
          {mappings.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No mappings configured. {sourceFields.length === 0 ? 'Send a test webhook to detect source fields.' : 'Add a mapping to get started.'}
            </div>
          ) : (
            <div className="space-y-2">
              {mappings.map((m, i) => (
                <div key={i} data-testid={`mapping-row-${i}`} className="grid grid-cols-12 gap-3 items-center bg-slate-50 border border-border rounded-sm p-3">
                  <div className="col-span-3">
                    {sourceFields.length > 0 ? (
                      <Select value={m.source_field} onValueChange={v => updateMapping(i, 'source_field', v)}>
                        <SelectTrigger className="rounded-sm h-8 text-xs">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {sourceFields.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={m.source_field} onChange={e => updateMapping(i, 'source_field', e.target.value)} placeholder="Source field" className="rounded-sm h-8 text-xs" />
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <div className="col-span-3">
                    <Input value={m.target_field} onChange={e => updateMapping(i, 'target_field', e.target.value)} placeholder="e.g. employee_name" className="rounded-sm h-8 text-xs font-data" />
                  </div>
                  <div className="col-span-2">
                    <Select value={m.data_type} onValueChange={v => updateMapping(i, 'data_type', v)}>
                      <SelectTrigger className="rounded-sm h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Select value={m.category} onValueChange={v => updateMapping(i, 'category', v)}>
                      <SelectTrigger className="rounded-sm h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm text-muted-foreground hover:text-red-600" onClick={() => removeMapping(i)} data-testid={`remove-mapping-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unmapped Fields Policy */}
      <Card className="border border-border rounded-sm shadow-sm">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Unmapped Fields Policy</p>
            <p className="text-xs text-muted-foreground">What to do with fields not in the mapping</p>
          </div>
          <Select value={unmappedAction} onValueChange={setUnmappedAction}>
            <SelectTrigger className="w-[150px] rounded-sm" data-testid="unmapped-policy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="store">Store Raw</SelectItem>
              <SelectItem value="ignore">Ignore</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );
}
